import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Award, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function QuizResult() {
  const { attemptId } = useParams();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResults();
  }, [attemptId]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('studentToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/quiz-attempts/${attemptId}/results`, getAuthHeaders());
      setResults(response.data);
    } catch (error) {
      console.error('Error fetching results:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm text-center py-12">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-blue border-r-transparent"></div>
        <p className="mt-4 text-slate-600">Loading results...</p>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm text-center py-12">
        <p className="text-slate-600">Results not found</p>
      </div>
    );
  }

  const { attempt, results: questionResults } = results;
  const percentage = (attempt.score / attempt.total_questions) * 100;
  const passed = percentage >= 60;

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="mb-8">
        <Link to="/student/dashboard">
          <Button variant="ghost" className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Quizzes
          </Button>
        </Link>

        <div className="text-center mb-8">
          <Award className={`h-20 w-20 mx-auto mb-4 ${passed ? 'text-green-600' : 'text-orange-600'}`} />
          <h1 className="text-3xl font-bold text-brand-dark mb-2">{attempt.quiz_title}</h1>
          <p className="text-xl text-slate-600">Quiz Results</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-blue-50 p-6 rounded-lg text-center">
            <p className="text-sm text-slate-600 mb-1">Score</p>
            <p className="text-3xl font-bold text-brand-blue">
              {attempt.score}/{attempt.total_questions}
            </p>
          </div>
          <div className="bg-green-50 p-6 rounded-lg text-center">
            <p className="text-sm text-slate-600 mb-1">Percentage</p>
            <p className="text-3xl font-bold text-green-600">{percentage.toFixed(1)}%</p>
          </div>
          <div className={`${passed ? 'bg-green-50' : 'bg-orange-50'} p-6 rounded-lg text-center`}>
            <p className="text-sm text-slate-600 mb-1">Status</p>
            <p className={`text-2xl font-bold ${passed ? 'text-green-600' : 'text-orange-600'}`}>
              {passed ? 'Passed' : 'Keep Practicing'}
            </p>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-brand-dark mb-6">Question-wise Analysis</h2>

        <div className="space-y-6">
          {questionResults.map((result, index) => (
            <div
              key={index}
              className={`border-2 rounded-lg p-6 ${
                result.is_correct ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold text-brand-dark flex-1">
                  Q{index + 1}. {result.question}
                </h3>
                {result.is_correct ? (
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 ml-4" />
                ) : (
                  <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 ml-4" />
                )}
              </div>

              <div className="space-y-2 mb-4">
                {result.options.map((option, optIndex) => (
                  <div
                    key={optIndex}
                    className={`p-3 rounded-lg ${
                      optIndex === result.correct_answer
                        ? 'bg-green-100 border-2 border-green-500'
                        : optIndex === result.student_answer && !result.is_correct
                        ? 'bg-red-100 border-2 border-red-500'
                        : 'bg-white border border-slate-200'
                    }`}
                  >
                    <span className="font-medium mr-2">{String.fromCharCode(65 + optIndex)}.</span>
                    <span>{option}</span>
                    {optIndex === result.correct_answer && (
                      <span className="ml-2 text-green-600 font-medium">(Correct Answer)</span>
                    )}
                    {optIndex === result.student_answer && !result.is_correct && (
                      <span className="ml-2 text-red-600 font-medium">(Your Answer)</span>
                    )}
                  </div>
                ))}
              </div>

              {result.explanation && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm font-semibold text-blue-900 mb-1">Explanation:</p>
                  <p className="text-sm text-blue-800">{result.explanation}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 text-center">
        <Link to="/student/dashboard">
          <Button data-testid="back-to-quizzes-btn" size="lg" className="bg-brand-blue hover:bg-blue-600">
            Back to Quiz List
          </Button>
        </Link>
      </div>
    </div>
  );
}
